import React, {useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {PaymentMethod as PaymentMethodEnum} from 'grpc-bob/enum_pb';

import PaymentSupport from '../../components/PaymentSupport';
import WithBorder from '../../components/WithBorder';

import {useTranslation} from 'react-i18next';
import {useDispatchCtx} from '../../context';
import {useEffectOnce} from 'react-use';

import SubscriptionService from '../../services/subscription';
import {AppState, setMessage} from '../../state-management/app';
import getKnowNative, {MIXPANEL} from '../../externals/mobile-communication';

import {appendQuery, ClientStorage, getMsgFromErr, parseQueryToField,} from '../../utils';
import {PAYMENT_ATM, PAYMENT_COD, PAYMENT_VISA_MASTERCARD} from '../../constants/render';
import {PaymentMethodGateWayMap, PaymentMethodKeyReverse} from '../../constants/map';

import styles from './PaymentMethod.module.css';


interface PaymentMethodProps extends AppState, RouteComponentProps {
}


const PaymentMethod = (props: PaymentMethodProps) => {
  const [redirectName, setRedirectName] = useState<null | PaymentMethodEnum>(null);
  const dispatch = useDispatchCtx();
  const {t} = useTranslation();

  const {token} = props;

  useEffectOnce(() => {
    const {Ref: orderIdInUrl} = parseQueryToField(props.location.search, ['Ref']);

    if (!props.paymentOrderId && !orderIdInUrl) {
      dispatch(setMessage('pleaseChoosePackage'));
      props.history.push(`/package${props.location.search}`);
    }

    // for currentStep = 2 mobile
    getKnowNative('options');
  });

  const onChoosePaymentMethod = async (whereToGo: PaymentMethodEnum) => {

    if (redirectName) return;
    setRedirectName(whereToGo);

    try {
      const paymentMethodStr = PaymentMethodKeyReverse[whereToGo];

      //mixpanel
      getKnowNative('mixpanel', undefined, {
        type: MIXPANEL.PAYMENT_METHOD_SELECTED,
        paymentMethod: paymentMethodStr
      });

      const {Ref} = parseQueryToField(props.location.search, ['Ref']);
      const orderId = props.paymentOrderId || Ref;

      switch (whereToGo) {
        case PaymentMethodEnum.PAYMENT_METHOD_COD:
          return props.history.push(`/cod?${appendQuery(props.location.search, 'Ref', String(orderId))}`);

        case PaymentMethodEnum.PAYMENT_METHOD_ATM:
        case PaymentMethodEnum.PAYMENT_METHOD_CREDIT_DEBIT_CARD:
          await goToAtmGateway(Number(orderId as string || '0'), whereToGo);
          break;
        default:
      }
    } catch (e) {
      if (e.message.includes('ORDER_TRANSITION_PROCESS_PAYMENT') && e.message.includes('ORDER_STATUS_CANCELED')) {
        dispatch(setMessage('tryAgain2Time'));
        return props.history.push(`package${props.location.search}`);
      }

      if (e.message.includes('ORDER_STATUS_FAILED') || e.message.includes('rpc error: code = PermissionDenied desc = can not update payment method for order ORDER_STATUS_CANCELED')) { //err contain this status, redirect to summary page
        dispatch(setMessage('paymentTimeout'));
        return props.history.push(`summary${props.location.search}`);
      }

      if (e.message.includes('rpc error: code = PermissionDenied desc = can not update payment method for order ORDER_STATUS_PROCESSING_PAYMENT')) {
        dispatch(setMessage('rePurchasePayment'));
        return props.history.push(`summary${props.location.search}`);
      }

      setRedirectName(null);
      dispatch(setMessage(getMsgFromErr(e)))
    }
  };

  const goToAtmGateway = async (orderId: number, paymentMethod: PaymentMethodEnum.PAYMENT_METHOD_ATM | PaymentMethodEnum.PAYMENT_METHOD_CREDIT_DEBIT_CARD) => {
    if (!orderId) { //null or NaN or 0
      return dispatch(setMessage('cannotFindOrderId'));
    }

    const {paymentUrl} = await SubscriptionService.updatePaymentMethodForOrder(
      orderId,
      paymentMethod,
      props.token as string
    );

    if (!paymentUrl) {
      return dispatch(setMessage('cannotCreateOrder'))
    }

    // dispatch(setPaymentUrl(paymentUrl));

    // let redirectUrl = paymentUrl;
    //
    // if (!redirectUrl) {
    //   const orderProcessing = ClientStorage.orderProcessing;
    //
    //   if (orderProcessing && orderProcessing.orderId === orderId) {
    //     redirectUrl = orderProcessing.paymentUrl;
    //   } else {
    //
    //   }
    // }


    ClientStorage.token = token!;
    ClientStorage.orderProcessing = {
      orderId: orderId,
      paymentMethod: PaymentMethodKeyReverse[paymentMethod],
      paymentUrl: paymentUrl
    };

    const [host, query] = paymentUrl.split('?');
    return window.location.href = `${host}?${appendQuery(query, 'payMethod', PaymentMethodGateWayMap[paymentMethod])}`;
  };

  return (
    <div className={styles.root}>
      <WithBorder
        className={styles.title}
        borders="top bottom"
        borderStyle="1px solid #E2E2E2"
      >
        {t('selectPaymentMethod')}
      </WithBorder>
      <WithBorder
        className={styles.methodSpacing}
        borders="top bottom"
        borderStyle="1px solid #E2E2E2"
      >
        <PaymentSupport
          method={t('atmCard')}
          supports={PAYMENT_ATM}
          isLoading={redirectName === PaymentMethodEnum.PAYMENT_METHOD_ATM}
          onClick={() => onChoosePaymentMethod(PaymentMethodEnum.PAYMENT_METHOD_ATM)}
        />
      </WithBorder>
      <WithBorder
        className={styles.methodSpacing}
        borders="top bottom"
        borderStyle="1px solid #E2E2E2"
      >
        <PaymentSupport
          method={t('visa')}
          supports={PAYMENT_VISA_MASTERCARD}
          isLoading={redirectName === PaymentMethodEnum.PAYMENT_METHOD_CREDIT_DEBIT_CARD}
          onClick={() => onChoosePaymentMethod(PaymentMethodEnum.PAYMENT_METHOD_CREDIT_DEBIT_CARD)}
        />
      </WithBorder>
      <WithBorder
        className={styles.methodSpacing}
        borders="top bottom"
        borderStyle="1px solid #E2E2E2"
      >
        <PaymentSupport
          method={t('cod')}
          supports={PAYMENT_COD}
          isLoading={redirectName === PaymentMethodEnum.PAYMENT_METHOD_COD}
          onClick={() => onChoosePaymentMethod(PaymentMethodEnum.PAYMENT_METHOD_COD)}
        />
      </WithBorder>
    </div>
  );
};

export default PaymentMethod;
